import React, { useState } from "react";

import {
  Dialog,
  ErrorMessage,
  PrimaryButton,
  SingleSelect,
  Table,
  Textfield,
} from "widgets";
import {
  useFetchBankDetails,
  useFetchCashiersCashBalance,
  useFetchCenters,
  useSubmitCashDeposit,
} from "store/backend/assets";
import CashDenominationForm from "components/CashDenominationForm/CashDenominationForm";
import {
  CENTER_CASHIER_LIST_COLUMNS,
  ORGANIZATIONS_SELECT_OPTIONS,
} from "constants/constants";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import SecondaryButton from "widgets/Buttons/SecondaryButton";

import styles from "./CashDeposit.module.scss";

const handleChangeCashDenominations = (value, unitValue, setFieldValue) => {
  const fieldName = `denominationDetails.count_${unitValue}`;

  if (value === "") {
    setFieldValue(fieldName, "");
    return;
  }

  const count = parseInt(value);

  if (!isNaN(count)) {
    setFieldValue(fieldName, Math.max(0, count));
  }
};

const prepareCashierTableData = (
  values,
  setFieldValue,
  cashierCashBalance,
  bankDetails
) => {
  if (!bankDetails) {
    return {
      finalRows: [],
    };
  }
  const cashierList = cashierCashBalance || [];

  const totalCashAvailable = cashierList.reduce(
    (sum, c) => sum + c.cash_in_hand,
    0
  );
  const totalDepositAmount = Object.values(values.cashierDeposits).reduce(
    (sum, val) => sum + (Number(val) || 0),
    0
  );

  const cashierRows = cashierList.map((cashier) => {
    const cashierId = cashier.cashier_crn_id;
    const currentDeposit = values.cashierDeposits[cashierId] || 0;
    const balance = cashier.cash_in_hand - currentDeposit;

    return {
      cashierName: cashier.cashier_name,
      cashInHand: `₹ ${cashier.cash_in_hand.toFixed(2)}`,
      depositAmount: (
        <Textfield
          value={currentDeposit}
          onChange={(e) => {
            const enteredAmount = Number(e.target.value);

            if (!Number.isInteger(enteredAmount) || enteredAmount < 0) return;

            const maxAllowed = Math.floor(cashier.cash_in_hand);
            const validDepositAmount = Math.min(enteredAmount, maxAllowed);

            setFieldValue(`cashierDeposits.${cashierId}`, validDepositAmount);

            const newTotalDeposit =
              totalDepositAmount - currentDeposit + validDepositAmount;
            const newRemainingCash = totalCashAvailable - newTotalDeposit;

            setFieldValue("totalDepositAmount", newTotalDeposit);
            setFieldValue("totalBalanceCampusCash", newRemainingCash);
          }}
        />
      ),
      balanceCashInHand: `₹ ${balance.toFixed(2)}`,
    };
  });

  return {
    finalRows: [
      ...cashierRows,
      {
        cashierName: <strong>TOTAL</strong>,
        cashInHand: <strong>₹ {totalCashAvailable.toFixed(2)}</strong>,
        depositAmount: <strong>₹ {totalDepositAmount.toFixed(2)}</strong>,
        balanceCashInHand: (
          <strong>
            ₹ {(totalCashAvailable - totalDepositAmount).toFixed(2)}
          </strong>
        ),
        isTotal: true,
      },
    ],
  };
};
const submitCashDepositPayloadBuilder = (values, cashierCashBalance) => {
  const cash_denominations = Object.entries(values.denominationDetails)
    .map(([key, count]) => {
      const denomination = parseInt(key.replace("count_", ""), 10);
      const countValue = parseInt(count, 10) || 0;
      return { denomination, count: countValue };
    })
    .filter((item) => item.count > 0);

  const cashier_deposits = (cashierCashBalance || []).map((cashier) => ({
    cashier_crn_id: cashier.cashier_crn_id,
    deposited_amount:
      parseFloat(values.cashierDeposits[cashier.cashier_crn_id]) || 0,
  }));

  return {
    tenant_id: Number(values.centerId),
    billing_entity_id: Number(values.organizationId),
    deposited_by_name: values.depositedBy.trim(),
    agent_id: localStorage.getItem("cashierCRNId"),
    total_deposited_amount: values.totalDepositAmount,
    total_cash_in_hand_after_deposit: values.totalBalanceCampusCash,
    cash_denominations,
    cashier_deposits,
    bank_details: {
      bank_id: Number(values.depositBankId),
      branch_id: Number(values.depositBankBranchId),
      account_id: Number(values.depositBankAccountNumber),
    },
  };
};

const handleOnClickConfirmSubmitCashDepositButton = (
  values,
  submitCashDeposit,
  openSnackbar,
  setDialogContent,
  cashierCashBalance
) => {
  const payload = submitCashDepositPayloadBuilder(values, cashierCashBalance);
  submitCashDeposit(payload, {
    onSuccess: () => {
      openSnackbar("Successfully submitted cash deposit", "success");
      setDialogContent(null);
      window.location.reload();
    },
    onError: (error) => {
      openSnackbar(
        error?.message ||
          "An unexpected error occurred while processing the request. Please try again.",
        "error"
      );
    },
  });
};
const handleDialogClose = (setDialogContent) => () => {
  setDialogContent(null);
};
const handleOnClickSubmitButton = (setDialogContent) => {
  setDialogContent(true);
};
const CashDepositForm = (props) => {
  const { openSnackbar } = useSnackbarContext();

  const { values, setFieldValue, errors, isValid } = props;
  const [dialogContent, setDialogContent] = useState(null);

  const {
    isPending: isFetchCentersLoading,
    data: centers,
    isError: errorOnFetchCenters,
    error: errorMessageOnFetchCenters,
  } = useFetchCenters();

  const CENTERS = Object.entries(centers || {}).map(([uuid, details]) => ({
    id: details.id,
    value: details.id,
    label: details.name,
  }));

  const {
    isPending: isFetchBankDetailsLoading,
    data: bankDetails,
    isError: errorOnFetchBankDetails,
    error: errorMessageOnFetchBankDetails,
  } = useFetchBankDetails(values.organizationId, values.centerId);

  const isOrganizationAndCenterSelected =
    values.organizationId && values.centerId;

  const {
    isPending: isFetchCashierCashBalanceLoading,
    data: cashierCashBalance,
    isError: isErrorOnFetchinCashierCashBalance,
    error: errorMessageOnCashierCashBalance,
  } = useFetchCashiersCashBalance(values.centerId, values.organizationId);

  const { mutate: submitCashDeposit, isPending: isSubmitCashDepositPending } =
    useSubmitCashDeposit();

  if (errorOnFetchBankDetails || errorMessageOnFetchBankDetails) {
    openSnackbar(
      errorMessageOnFetchBankDetails.message ||
        "An unexpected error occurred while processing the request. Please try again.",
      "error"
    );
  }

  const { finalRows } = prepareCashierTableData(
    values,
    setFieldValue,
    cashierCashBalance,
    bankDetails
  );

  const BANK_OPTIONS = bankDetails?.map((bank) => ({
    id: bank.id,
    value: bank.id,
    label: bank.bank_name,
  }));

  const selectedBank = bankDetails?.find(
    (b) => b.id === Number(values.depositBankId)
  );

  const BRANCH_OPTIONS = (selectedBank?.branches || []).map((branch) => ({
    id: branch.id,
    value: branch.id,
    label: branch.branch_name,
  }));

  const selectedBranch = selectedBank?.branches?.find(
    (br) => br.id === Number(values.depositBankBranchId)
  );

  const ACCOUNT_OPTIONS = (selectedBranch?.accounts || []).map((acc) => ({
    id: acc.id,
    value: acc.id,
    label: acc.account_number,
  }));
  const denominationTotal = Object.entries(values.denominationDetails).reduce(
    (acc, [key, count]) => {
      const unit = parseInt(key.replace("count_", ""), 10);
      return acc + unit * (Number(count) || 0);
    },
    0
  );

  const isMathMismatch = denominationTotal !== values.totalDepositAmount;

  return (
    <div className={styles.cashDepositForm}>
      <div className={styles.cashierDepositEntryMain}>
        <h3 className={styles.sectionHeader}>Cashier Deposit Entry</h3>
        <div className={styles.cashDepositEntryContainer}>
          <div className={styles.flexContainer}>
            <span className={styles.label}>Organization Name</span>
            <SingleSelect
              tag="Organization Name"
              value={values.organizationId}
              menuItems={ORGANIZATIONS_SELECT_OPTIONS}
              handleChange={(e) =>
                setFieldValue("organizationId", e.target.value)
              }
            />
          </div>
          {isOrganizationAndCenterSelected && (
            <div className={styles.flexContainer}>
              <span className={styles.label}>Deposit Bank</span>
              <SingleSelect
                tag="Deposit Bank"
                value={values.depositBankId}
                menuItems={BANK_OPTIONS}
                handleChange={(e) =>
                  setFieldValue("depositBankId", e.target.value)
                }
                isLoading={isFetchBankDetailsLoading}
                disabled={Boolean(errorOnFetchBankDetails)}
              />
            </div>
          )}
          <div className={styles.flexContainer}>
            <span className={styles.label}>Center</span>
            <SingleSelect
              tag="Center"
              value={values.centerId}
              menuItems={CENTERS}
              handleChange={(e) => setFieldValue("centerId", e.target.value)}
            />
          </div>
          {isOrganizationAndCenterSelected && (
            <div className={styles.flexContainer}>
              <span className={styles.label}>Deposit Bank Branch</span>
              <SingleSelect
                tag="Deposit Bank Branch"
                value={values.depositBankBranchId}
                menuItems={BRANCH_OPTIONS}
                handleChange={(e) =>
                  setFieldValue("depositBankBranchId", e.target.value)
                }
                disabled={!values.depositBankId}
              />
            </div>
          )}
          <div className={styles.flexContainer}>
            <span className={styles.label}>Deposited By</span>
            <Textfield
              label="Deposited By"
              value={values.depositedBy}
              onChange={(e) => setFieldValue("depositedBy", e.target.value)}
            />
            {errors?.depositedBy && (
              <ErrorMessage>{errors.depositedBy} </ErrorMessage>
            )}
          </div>
          {isOrganizationAndCenterSelected && (
            <div className={styles.flexContainer}>
              <span className={styles.label}>Deposit Bank Account Number</span>
              <SingleSelect
                tag="Deposit Bank Account Number"
                value={values.depositBankAccountNumber}
                menuItems={ACCOUNT_OPTIONS}
                handleChange={(e) =>
                  setFieldValue("depositBankAccountNumber", e.target.value)
                }
                disabled={!values.depositBankBranchId}
              />
            </div>
          )}
        </div>
      </div>

      <div className={styles.centerCashierListContainer}>
        <h3 className={styles.sectionHeader}>Center Cashier List</h3>
        <Table columns={CENTER_CASHIER_LIST_COLUMNS} rows={finalRows} />
      </div>

      <div className={styles.cashDepositDenomination}>
        <h3 className={styles.sectionHeader}>Cash Deposit Denomination</h3>
        <CashDenominationForm
          values={values}
          amountDetails={[
            {
              label: "Total Center Collecting Amount For Deposit",
              amount: values.totalDepositAmount,
            },
            {
              label: "Total Center Balance Campus Cash In Hand",
              amount: values.totalBalanceCampusCash,
            },
          ]}
          amountToValidate={values.totalDepositAmount}
          amountLabel="Deposited Amount"
          handleChangeCashDenominations={(value, unitValue) =>
            handleChangeCashDenominations(value, unitValue, setFieldValue)
          }
        />
      </div>
      <div className={styles.submitButton}>
        <PrimaryButton
          onClick={() => handleOnClickSubmitButton(setDialogContent)}
          disabled={!isValid || isMathMismatch}
        >
          Submit
        </PrimaryButton>
      </div>
      <Dialog
        open={dialogContent !== null}
        handleClose={handleDialogClose(setDialogContent)}
      >
        <div className={styles.dialogContent}>
          <span className={styles.dialogMessage}>
            You’re about to submit these details. Once submitted, you won’t be
            able to edit or change this information. Please review everything
            carefully before proceeding.
          </span>
          <div className={styles.dialogActions}>
            <SecondaryButton onClick={handleDialogClose(setDialogContent)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              onClick={() =>
                handleOnClickConfirmSubmitCashDepositButton(
                  values,
                  submitCashDeposit,
                  openSnackbar,
                  setDialogContent,
                  cashierCashBalance
                )
              }
            >
              {isSubmitCashDepositPending ? "Submitting..." : "Submit"}
            </PrimaryButton>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default CashDepositForm;
